import React, { useState } from "react";
import { Input } from "../../../shared/components/Input.jsx";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";
import { maskAccountNumber, newBankAccountId } from "../models/company.js";
import { countAccountTransactions } from "../../banking/services/bankService.js";

/**
 * Edits a company's bank accounts (several allowed). The ★ primary one prints on invoices.
 * Removing an account that already has imported statement transactions is blocked.
 */
export function BankAccountsEditor({ accounts = [], onChange }) {
  const { confirmDanger } = useDangerConfirm();
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const update = (id, patch) => onChange(accounts.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  const addAccount = () => {
    const account = { id: newBankAccountId(), bankName: "", accountNumber: "", ifscCode: "", branchName: "", accountHolder: "", isPrimary: accounts.length === 0 };
    onChange([...accounts, account]);
    setOpenId(account.id);
  };

  const makePrimary = (id) => onChange(accounts.map((a) => ({ ...a, isPrimary: a.id === id })));

  const remove = async (account) => {
    setBusyId(account.id);
    try {
      const count = await countAccountTransactions(account.id).catch(() => 0);
      if (count > 0) {
        alert(`${account.bankName || "This account"} ${maskAccountNumber(account.accountNumber)} has ${count} imported bank transactions.\n\nUndo its statement imports on the Banking page first, then remove the account.`);
        return;
      }
      const ok = await confirmDanger({
        title: "Remove bank account?",
        message: `Remove ${account.bankName || "bank account"} ${maskAccountNumber(account.accountNumber)} from this company?`,
        confirmLabel: "Remove"
      });
      if (!ok) return;
      const rest = accounts.filter((a) => a.id !== account.id);
      if (account.isPrimary && rest.length) rest[0] = { ...rest[0], isPrimary: true };
      onChange(rest);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
        <div style={{ fontSize: "0.85rem", fontWeight: "700", color: "var(--text-dim)", textTransform: "uppercase" }}>
          🏦 Bank Accounts
        </div>
        <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: "0.72rem", padding: "2px 8px" }} onClick={addAccount}>
          + Add account
        </button>
      </div>

      {accounts.length === 0 && (
        <div style={{ fontSize: "0.8rem", color: "var(--text-dim)", fontStyle: "italic" }}>No bank account yet.</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {accounts.map((a) => {
          const open = openId === a.id;
          return (
            <div key={a.id} style={{ border: a.isPrimary ? "1.5px solid var(--primary)" : "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "0.6rem 0.75rem", background: "var(--bg-card)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
                <button type="button" onClick={() => setOpenId(open ? null : a.id)} style={{ background: "none", border: "none", padding: 0, textAlign: "left", color: "var(--text-main)", cursor: "pointer", flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.88rem" }}>
                    {a.bankName || "New account"} {a.accountNumber ? maskAccountNumber(a.accountNumber) : ""}
                    {a.isPrimary && <span style={{ marginLeft: "6px", fontSize: "0.7rem", color: "var(--primary)" }}>★ Primary</span>}
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-dim)" }}>
                    {[a.ifscCode, a.branchName].filter(Boolean).join(" • ") || "Tap to enter details"}
                  </div>
                </button>
                {!a.isPrimary && (
                  <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: "0.68rem", padding: "2px 6px" }} onClick={() => makePrimary(a.id)}>
                    ★ Make primary
                  </button>
                )}
                <button type="button" className="btn-icon" aria-label="Remove bank account" disabled={busyId === a.id} onClick={() => remove(a)} style={{ color: "var(--danger)" }}>
                  ✕
                </button>
              </div>

              {open && (
                <div style={{ marginTop: "0.6rem" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
                    <Input id={`bank-${a.id}-name`} label="Bank Name" placeholder="e.g. TMB BANK" value={a.bankName} onChange={(e) => update(a.id, { bankName: e.target.value })} />
                    <Input id={`bank-${a.id}-number`} label="Account Number" placeholder="e.g. 010700150950189" value={a.accountNumber} onChange={(e) => update(a.id, { accountNumber: e.target.value.replace(/\s+/g, "") })} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
                    <Input id={`bank-${a.id}-ifsc`} label="IFSC Code" placeholder="e.g. TMBL0000010" value={a.ifscCode} onChange={(e) => update(a.id, { ifscCode: e.target.value.toUpperCase() })} />
                    <Input id={`bank-${a.id}-branch`} label="Branch" placeholder="e.g. Muhavoor" value={a.branchName} onChange={(e) => update(a.id, { branchName: e.target.value })} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <span style={{ fontSize: "0.72rem", color: "var(--text-dim)", display: "block", marginTop: "4px" }}>
        The ★ primary account prints on invoices and receipts. Statements for any account can be imported on the Banking page.
      </span>
    </div>
  );
}

export default BankAccountsEditor;
